import { FormInstance } from "antd";
import { makeAutoObservable } from "mobx";
import { getEntityCachedRequest } from "src/common/entity/getEntityCachedRequest";
import { onError } from "src/common/onError";
import { DefaultTSettingsStore } from "src/pages/EntityFiltersPage/DefaultTSettingsStore";
import {
  EntFilterActionsMeta,
  EntFilterActionType,
} from "src/pages/EntityFiltersPage/EntityFiltersPage.types";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import {
  EntityFilters,
  ZEntityRow,
} from "src/pages/EntityFiltersPage/EntityList/types";
import { getLatestEntityTSettings } from "src/pages/EntityFiltersPage/EntityTableStrategy";
import { loadFilterAttributes } from "src/pages/ManagementPage/Obj2Tab/forms/AttrForm2/SelectFilter/loadFilterAttributes";
import { TableSettingsType } from "src/pages/ManagementPage/Obj2Tab/ObjTableSettings/ObjTableSettingsTypes";
import { ZAttribute } from "src/types/ZAttribute";
import { ZUid } from "src/types/zUid";
import { userPreferenceKeys } from "src/common/userPreferenceKeys";
import { selectOnRowClick } from "../tables/selectOnRowClick";
import { makeBacklinkFilterRestriction } from "./makeBacklinkFilterRestriction";

interface ParamsInitEntitySelectTableStore {
  attribute: ZAttribute;
  multiple: boolean;
  form: FormInstance;
  value?: string[];
  actionsList?: EntFilterActionType[];
  actionsMeta?: EntFilterActionsMeta;
  entityId?: ZUid;
}

export class EntitySelectTableStore {
  constructor(
    private onChange?: (value?: string[]) => void,
    private onSetValues?: () => void,
  ) {
    makeAutoObservable(this);
  }

  filtersPageStore: EntityFiltersPageStore | null = null;

  setFilterPageStore(store: EntityFiltersPageStore) {
    this.filtersPageStore = store;
  }

  initialValues: string[] | null = null;

  setInitialValues(list: string[]) {
    this.initialValues = list;
  }

  open: boolean = false;

  setOpen(flag: boolean) {
    this.open = flag;
  }

  values: string[] = [];

  setValues(list: string[]) {
    this.values = list;
    this.onSetValues?.();
  }

  filter: ZAttribute | null = null;

  setFilter(newFilter: ZAttribute | null) {
    this.filter = newFilter;
  }

  async init({
    attribute,
    multiple,
    form,
    value,
    actionsList,
    actionsMeta,
    entityId,
  }: ParamsInitEntitySelectTableStore) {
    try {
      const { filter, referenceId, id: attrId } = attribute;
      const safeValues = value || [];
      this.setInitialValues(safeValues);
      if (filter) {
        const filterAttr = (await loadFilterAttributes(attrId)).find(
          ({ id }) => id === filter,
        );
        this.setFilter(filterAttr ?? null);
      }
      const actions = new Set([
        EntFilterActionType.changeState,
        EntFilterActionType.export,
        ...(actionsList ?? []),
      ]);

      const initialFilters = this.getFilters(form) ?? undefined;
      const filterRestrictions = makeBacklinkFilterRestriction(
        attribute.backlinkAttributeId,
        entityId,
      );
      this.setFilterPageStore(
        new EntityFiltersPageStore({
          objectId: referenceId ?? undefined,
          selectionSettings: {
            keepSelected: true,
            selectionType: multiple ? "checkbox" : "radio",
          },

          save: () => this.saveSafeParams(referenceId ?? undefined),
          initialFilters,
          filterRestrictions,
          /* Ограничение по обратным ссылкам привязано к текущему экземпляру,
             поэтому его нельзя сохранять в общих настройках объекта. */
          persistFilterState: !filterRestrictions,
          actions,
          onRowClick: (row) => {
            const store = this.filtersPageStore?.tableStore;
            if (store) selectOnRowClick(row, store);
          },
          defTSettingsStore: new DefaultTSettingsStore(
            TableSettingsType.valueObjectId,
          ),
          actionsMeta,
          onNewRow: () => this.handleOk(),
          onStartCreate: () => this.setOpen(false),
        }),
      );
    } catch (error) {
      onError(error);
    }
  }

  async initSelected(values: string[]) {
    const tableStore = this.filtersPageStore?.tableStore;
    if (!tableStore) return;
    /**
     * чтобы отрисовать все элементы в KeepSelected, нужно получить данные не только
     * согласно пагинации, но еще и данные с других страниц, поэтому мы просто запрашиваем все
     * экземпляры, которые выбраны в данный момент. По-хорошему тут нужен отдельный запрос, который вернет все разом.
     */
    const entities = await Promise.all(values.map(getEntityCachedRequest));
    const uniqRows = new Map(entities.map((e) => [e.id, e]));
    const toSelect = values
      .map((e) => uniqRows.get(e))
      .filter(Boolean) as ZEntityRow[];
    tableStore?.setSelected(toSelect);
  }

  handleCancel() {
    this.initSelected(this.initialValues || []);
    this.setOpen(false);
  }

  handleOk() {
    const newValues = this.filtersPageStore?.tableStore?.selected.map(
      ({ id }) => String(id),
    );
    this.onChange?.(newValues || []);
    this.setInitialValues(newValues || []);
    this.setOpen(false);
  }

  getFilters(form: FormInstance): EntityFilters | null {
    const { filter } = this;
    if (!filter) return null;
    const { id } = filter;
    const filterValue: string[] | null | undefined = form.getFieldValue(
      String(id),
    );
    return {
      attributeId: filterValue?.length ? id : undefined,
      restrictionValues: filterValue?.length ? filterValue : undefined,
    };
  }

  addFilters(form: FormInstance) {
    const { filtersPageStore } = this;
    if (!filtersPageStore) return;
    const restrFilters = this.getFilters(form);
    filtersPageStore.setInitialFilters(restrFilters);
    const { tableStore } = filtersPageStore;
    if (!tableStore) return;
    const currentFilters = tableStore.filters;
    if (!currentFilters) return;
    tableStore.setFilters({ ...currentFilters, ...restrFilters });
  }

  async saveSafeParams(objectId?: ZUid) {
    const { filtersPageStore } = this;

    if (!objectId) return;
    const params = filtersPageStore?.tableStore?.params;
    const config = filtersPageStore?.tableConfig;

    if (!params) return;
    config?.save(
      userPreferenceKeys.enitiesSearchLP(objectId),
      getLatestEntityTSettings({
        ...params,
        filters: {
          ...params.filters,
          attributeId: undefined,
          restrictionValues: undefined,
          hierarchicalFilter: filtersPageStore?.persistFilterState
            ? params.filters?.hierarchicalFilter
            : undefined,
        },
      }),
    );
  }
}
